import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { Card, Chart, Empty, Notice, Stat } from '../components.js';
import { formatDate, percent, useAction, useAsync } from '../hooks.js';

const WINDOWS = [7, 30, 90];

export function Dashboard() {
  const [windowDays, setWindowDays] = useState(30);
  const stats = useAsync(() => api.stats(windowDays), [windowDays]);
  const timeline = useAsync(() => api.timeline(windowDays), [windowDays]);
  const posts = useAsync(() => api.posts(), []);
  const { run, busy, error } = useAction();
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * Déclenche ce que ferait le cron horaire. Les crons ne sont pas actifs (le
   * plan Workers Free plafonne à 5 déclencheurs, tous consommés) : c'est donc
   * aujourd'hui le seul moyen de lancer une passe complète.
   */
  const collect = async () => {
    setNotice(null);
    const result = await run(() => api.runCollection());
    if (!result) return;
    setNotice(
      result.scheduled === 0
        ? 'Aucune source échue : toutes ont été collectées récemment.'
        : `${result.scheduled} source(s) lancée(s) en mode ${result.mode}. ` +
          (result.mode === 'direct'
            ? 'La collecte tourne en arrière-plan ; rechargez dans quelques minutes.'
            : 'Le traitement se fait par la file.'),
    );
    stats.reload();
  };

  const sync = async () => {
    const result = await run(() => api.syncUmami());
    if (result) {
      posts.reload();
      stats.reload();
    }
  };

  return (
    <>
      <header className="topbar">
        <h1>Tableau de bord</h1>
        <span className="spacer" />
        <div className="inline">
          {WINDOWS.map((days) => (
            <button
              key={days}
              type="button"
              className={days === windowDays ? 'primary small' : 'small'}
              onClick={() => setWindowDays(days)}
            >
              {days} j
            </button>
          ))}
          <button type="button" className="primary small" onClick={collect} disabled={busy}>
            {busy ? 'En cours…' : 'Lancer la collecte'}
          </button>
          <button type="button" className="small" onClick={sync} disabled={busy}>
            Synchroniser Umami
          </button>
        </div>
      </header>

      <div className="content">
        {error ? <Notice kind="error">{error}</Notice> : null}
        {notice ? <Notice kind="success">{notice}</Notice> : null}
        {stats.error ? <Notice kind="error">{stats.error}</Notice> : null}

        {stats.data ? (
          <>
            <div className="grid cols-4">
              <Stat label="Discussions détectées" value={stats.data.hitsDiscovered} />
              <Stat
                label="Retenues"
                value={stats.data.hitsRelevant}
                hint={`taux de réponse ${percent(stats.data.responseRate)}`}
              />
              <Stat label="Publiées" value={stats.data.posted} hint={`${stats.data.drafted} brouillon(s)`} />
              <Stat
                label="Visites"
                value={stats.data.visits}
                hint={`${stats.data.visitsPerPost.toFixed(1)} par publication`}
              />
            </div>

            {stats.data.posted > 0 && stats.data.visitsPerPost < 5 ? (
              <Notice kind="rule">
                Moins de 5 visites par publication : à ce niveau, le canal ne vaut probablement pas
                les 30 min/semaine qu’il coûte. Regardez d’abord la précision des sources ci-dessous.
              </Notice>
            ) : null}

            <Card title={`Découvertes sur ${windowDays} jours`}>
              {timeline.data ? <Chart points={timeline.data} /> : <Empty>Chargement…</Empty>}
              <div className="small muted inline" style={{ marginTop: 8 }}>
                <span style={{ color: 'var(--accent)' }}>■</span> détectées
                <span style={{ color: 'var(--ok)' }}>■</span> retenues
              </div>
            </Card>

            <div className="grid cols-2">
              <Card title="Sources, triées par précision" tight>
                <table>
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th className="num">Détectées</th>
                      <th className="num">Retenues</th>
                      <th className="num">Précision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...stats.data.bySource]
                      .sort((a, b) => b.precision - a.precision || b.hits - a.hits)
                      .map((source) => (
                        <tr key={source.sourceId}>
                          <td>
                            <span className="badge">{source.platformKind}</span>{' '}
                            <span className="mono">{source.sourceValue}</span>
                          </td>
                          <td className="num">{source.hits}</td>
                          <td className="num">{source.kept}</td>
                          <td className="num">
                            {source.hits === 0 ? (
                              <span className="muted">—</span>
                            ) : (
                              <strong>{percent(source.precision)}</strong>
                            )}
                          </td>
                        </tr>
                      ))}
                    {stats.data.bySource.length === 0 ? (
                      <tr>
                        <td colSpan={4}>
                          <Empty>
                            Aucune source. <Link to="/configuration">Configurez un site</Link>.
                          </Empty>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </Card>

              <Card title="Par site" tight>
                <table>
                  <thead>
                    <tr>
                      <th>Site</th>
                      <th className="num">Détectées</th>
                      <th className="num">Publiées</th>
                      <th className="num">Visites</th>
                      <th className="num">Inscriptions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.data.bySite.map((site) => (
                      <tr key={site.siteId}>
                        <td>{site.siteName}</td>
                        <td className="num">{site.hits}</td>
                        <td className="num">{site.posted}</td>
                        <td className="num">{site.visits}</td>
                        <td className="num">{site.signups}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          </>
        ) : null}

        <Card title="Publications et rendement" tight>
          <table>
            <thead>
              <tr>
                <th>Discussion</th>
                <th>Site</th>
                <th>Publiée</th>
                <th className="num">Visites</th>
                <th className="num">Inscriptions</th>
                <th className="num">Outil gratuit</th>
                <th className="num">Payant</th>
              </tr>
            </thead>
            <tbody>
              {(posts.data ?? []).map((row) => (
                <tr key={row.post.id}>
                  <td>
                    <a href={row.post.url ?? row.hitUrl} target="_blank" rel="noreferrer">
                      {row.hitTitle}
                    </a>
                    <div className="small muted mono truncate">{row.post.utmCampaign}</div>
                  </td>
                  <td>{row.siteName}</td>
                  <td className="small">{formatDate(row.post.postedAt)}</td>
                  <td className="num">{row.metrics.visits}</td>
                  <td className="num">{row.metrics.signups}</td>
                  <td className="num">{row.metrics.freeToolUses}</td>
                  <td className="num">{row.metrics.paid}</td>
                </tr>
              ))}
              {posts.data && posts.data.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <Empty>
                      Aucune publication enregistrée. Une fois une réponse publiée à la main,
                      enregistrez-la depuis le détail du hit pour en mesurer le rendement.
                    </Empty>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
